import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { StickerQR } from './StickerQR';
import { X, Printer, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URL
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  try {
    if (url.includes('%')) return url;
    return encodeURI(url);
  } catch {
    return url;
  }
};

// Warranty class definitions
const garantieClasses = {
  'A': '6 mois ou 10 000 km',
  'B': '3 mois ou 5 000 km',
  'C': '1 mois ou 1 700 km',
  'D': 'Pas de garantie (LPC 37,38)'
};

// Calculate warranty category based on Quebec law
const calculateWarrantyCategory = (vehicleYear, mileage) => {
  const currentYear = new Date().getFullYear();
  const vehicleAge = currentYear - vehicleYear;
  const km = parseInt(mileage) || 0;

  if (vehicleAge <= 4 && km <= 80000) return 'A';
  if (vehicleAge <= 5 && km <= 100000) return 'B';
  if (vehicleAge <= 7 && km <= 120000) return 'C';
  return 'D';
};

// Single Sticker Component for bulk printing
const SingleSticker = ({ vehicle, isLast }) => {
  const year = vehicle.year || '';
  const make = vehicle.make || '';
  const model = vehicle.model || '';
  const trim = vehicle.trim || '';
  const vin = vehicle.vin || '';
  const stock = vehicle.stock_number || '';
  const mileage = vehicle.mileage || '';
  const color = vehicle.exterior_color || '';
  const transmission = vehicle.transmission || 'Automatique';
  const fuelType = vehicle.fuel_type || 'Essence';
  const price = vehicle.regular_price || vehicle.price || '';
  const mainImage = vehicle.images?.[0] ? normalizeImageUrl(vehicle.images[0]) : null;
  const warrantyClass = calculateWarrantyCategory(vehicle.year, vehicle.mileage);

  return (
    <div 
      className="sticker-page bg-white" 
      style={{ 
        fontFamily: 'Arial, sans-serif', 
        padding: '10mm',
        pageBreakAfter: isLast ? 'auto' : 'always',
        breakAfter: isLast ? 'auto' : 'page'
      }}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-2 px-3 py-2 bg-blue-900 rounded-lg">
        <div>
          <div className="text-xl font-bold text-white">ENTREPÔT AUTO MONTRÉAL</div>
          <div className="text-xs text-white">1007 boul. René-Lévesque VERDUN, H3E 1H5 | (833) 995-6477</div>
        </div>
        <img src="/logo512.png" alt="Logo" className="h-16" />
      </div>

      {/* Title */}
      <div className="text-center mb-4">
        <h1 className="text-2xl font-bold text-white bg-blue-900 py-2 px-4 rounded-lg">
          {year} {make} {model} {trim}
        </h1>
      </div>

      {/* Photo + Price Row */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Car Photo */}
        <div>
          <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Photo du véhicule</div>
          <div className="border-2 border-gray-300 p-2 rounded-b-lg flex items-center justify-center" style={{ minHeight: '130px' }}>
            {mainImage ? (
              <img 
                src={mainImage} 
                alt={`${year} ${make} ${model}`}
                className="max-w-full object-contain rounded shadow vehicle-photo"
                style={{ maxHeight: '120px' }}
              />
            ) : (
              <div className="text-gray-400 text-center text-sm">Photo non disponible</div>
            )}
          </div>
        </div>
        
        {/* Price + Financing */}
        <div>
          <div className="mb-3">
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Prix de vente</div>
            <div className="border-2 border-gray-300 p-2 text-center rounded-b-lg">
              <div className="text-2xl font-bold text-gray-900">
                {price ? `${parseFloat(price).toLocaleString('fr-CA')}$` : 'Sur demande'}
              </div>
            </div>
          </div>
          <div>
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Financement</div>
            <div className="border-2 border-gray-300 p-1 text-center text-xs rounded-b-lg text-gray-900">
              Demandez au marchand
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left Column */}
        <div>
          {/* Garanties */}
          <div className="mb-3">
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Garanties</div>
            <div className="border-2 border-gray-300 p-2 text-xs rounded-b-lg text-gray-900">
              <p className="font-bold mb-1">Garantie légale</p>
              <p className="mb-1"><strong>Classe {warrantyClass}:</strong> {garantieClasses[warrantyClass]}</p>
              <p className="mb-1"><strong>Du vendeur:</strong> Demandez</p>
            </div>
          </div>

          {/* Utilisation antérieure */}
          <div className="mb-3">
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Utilisation antérieure</div>
            <div className="border-2 border-gray-300 p-2 text-xs rounded-b-lg text-gray-900">
              <p className="font-bold mb-1">Véhicule de promenade</p>
              <p><strong>Propriétaire:</strong> Particulier</p>
            </div>
          </div>

          {/* Réparations */}
          <div>
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Réparations</div>
            <div className="border-2 border-gray-300 p-1 text-xs rounded-b-lg text-gray-900">
              <p className="font-bold">Aucune</p>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div>
          {/* Description */}
          <div className="mb-3">
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Description du véhicule</div>
            <div className="border-2 border-gray-300 p-2 text-xs rounded-b-lg text-gray-900">
              <div className="grid grid-cols-2 gap-1" style={{ lineHeight: '1.5' }}>
                <div><strong>No unité:</strong></div><div>{stock || 'N/A'}</div>
                <div><strong>Année:</strong></div><div>{year || 'N/A'}</div>
                <div><strong>Marque:</strong></div><div>{make || 'N/A'}</div>
                <div><strong>Modèle:</strong></div><div>{model || 'N/A'}</div>
                <div><strong>Version:</strong></div><div>{trim || 'N/A'}</div>
                <div><strong>N° série:</strong></div><div className="text-[10px] break-all">{vin || 'N/A'}</div>
                <div><strong>Odomètre:</strong></div><div>{mileage ? `${parseInt(mileage).toLocaleString('fr-CA')} km` : 'N/A'}</div>
                <div><strong>Trans.:</strong></div><div>{transmission}</div>
                <div><strong>Carburant:</strong></div><div>{fuelType}</div>
                <div><strong>Couleur:</strong></div><div>{color || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Acceptation */}
          <div>
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-sm rounded-t-lg">Acceptation</div>
            <div className="border-2 border-gray-300 p-2 text-xs rounded-b-lg text-gray-900">
              <p className="mb-2 text-xs">Je reconnais avoir reçu une copie de l'étiquette</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs"><strong>Date:</strong></p>
                  <div className="border-b-2 border-black mt-2"></div>
                </div>
                <div>
                  <p className="text-xs"><strong>Signature:</strong></p>
                  <div className="border-b-2 border-black mt-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-3 pt-2 border-t-2 border-blue-900">
        <div className="flex justify-between items-end">
          <div>
            <p className="font-bold text-xs text-blue-900">www.entrepotautomontreal.ca</p>
            <p className="text-[10px] italic text-blue-800">« Redéfinir L'expérience Automobile »</p>
            <p className="text-[10px] text-gray-600 mt-1">* Prix excluent taxes et frais.</p>
          </div>
          <StickerQR vehicleId={vehicle.id} />
        </div>
      </div>
    </div>
  );
};

const BulkWindowSticker = ({ vehicles, onClose, onPrinted }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Small delay to allow rendering
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => {
    window.print();
    onPrinted?.(vehicles.map(v => v.id));
  };

  if (!vehicles || vehicles.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg">
          <div className="text-center text-red-600">Aucun véhicule sélectionné</div>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  // Portal to <body> so print CSS can drop the whole admin page (it caused dozens of duplicate pages)
  return createPortal(
    <div className="print-root fixed inset-0 bg-black bg-opacity-50 z-50 overflow-auto print:static print:bg-white print:block print:inset-auto print:overflow-visible bulk-sticker-wrapper">
      <style>{`
        @media print {
          body > *:not(.print-root) {
            display: none !important;
          }

          @page {
            margin: 6mm;
            size: A4 portrait;
          }
          
          * {
            overflow: visible !important;
          }
          
          body * {
            visibility: hidden !important;
          }
          
          .bulk-sticker-wrapper,
          .bulk-sticker-wrapper * {
            visibility: visible !important;
          }
          
          .no-print {
            display: none !important;
            visibility: hidden !important;
          }
          
          .bulk-sticker-wrapper {
            position: static !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: auto !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }
          
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }
          
          .sticker-page {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          .vehicle-photo {
            max-height: 100px !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .bg-blue-900 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Control Bar - Hidden on Print */}
      <div className="no-print sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 z-10">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">Impression en lot</h2>
            <p className="text-slate-400 text-sm">{vehicles.length} étiquette(s) sélectionnée(s)</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              data-testid="bulk-print-btn"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
              Imprimer tout
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-600 text-white rounded-lg hover:bg-slate-500 font-medium"
              data-testid="bulk-close-btn"
            >
              <X className="h-5 w-5" />
              Fermer
            </button>
          </div>
        </div>
      </div>

      {/* Preview List with Thumbnails - Hidden on Print */}
      <div className="no-print max-w-5xl mx-auto p-4">
        <div className="bg-slate-700 rounded-lg p-4 mb-4">
          <h3 className="text-white font-medium mb-3">Aperçu des véhicules à imprimer ({vehicles.length}):</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {vehicles.map((v, i) => {
              const imgUrl = v.images?.[0] ? normalizeImageUrl(v.images[0]) : null;
              return (
                <div key={v.id || i} className="bg-slate-600 rounded-lg overflow-hidden">
                  {/* Thumbnail */}
                  <div className="relative aspect-[16/10] bg-slate-500">
                    {imgUrl ? (
                      <img 
                        src={imgUrl} 
                        alt={`${v.year} ${v.make} ${v.model}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded font-bold">
                      {i + 1}
                    </span>
                  </div>
                  {/* Vehicle Info */}
                  <div className="p-2">
                    <p className="text-white text-sm font-medium truncate">{v.year} {v.make} {v.model}</p>
                    <p className="text-slate-400 text-xs truncate">{v.trim || 'N/A'} • #{v.stock_number || 'N/A'}</p>
                    <p className="text-green-400 text-xs font-medium mt-1">
                      {v.price ? `${parseFloat(v.price).toLocaleString('fr-CA')}$` : 'Sur demande'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stickers Container */}
      <div className="max-w-4xl mx-auto print:max-w-full">
        {loading ? (
          <div className="no-print text-center py-12">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-white">Préparation des étiquettes...</p>
          </div>
        ) : (
          vehicles.map((vehicle, index) => (
            <SingleSticker 
              key={vehicle.id || index} 
              vehicle={vehicle} 
              isLast={index === vehicles.length - 1}
            />
          ))
        )}
      </div>
    </div>
  , document.body);
};

export default BulkWindowSticker;
